import { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';
import { LYRIC_RESYNC_PER_HOUR, type LyricTimingV1 } from 'fym-shared/src/music24/lyricTiming';
import { fymAPI, useResyncLyricTimingMutation } from '../../../../../redux/api/fymAPI';
import type { StoreType } from '../../../../../redux/store';
import { lyricTimingRefusal, resyncRefusalMessage } from './lyricTimingRefusal';
import { LyricResyncNote, lineStartsOf, resyncDoneNote } from './resyncOutcome';

/** How long "Synced again" stays. An error stays until the next click. */
export const RESYNC_DONE_NOTE_MS = 10_000;

interface UseLyricResyncProps {
  accountId: number;
  itemUuid: string;
  /** The timing on the screen. When a re-sync of this page ends, the note says if the times changed. */
  timing: LyricTimingV1 | null | undefined;
  /** The lines that a member set by hand. Re-sync drops them, so it asks first (design s8). */
  editedCount: number;
  /** Drops the edits that wait to save, so no save runs into the sync (409 SYNCING). */
  discardPending: () => void;
}

/**
 * Re-sync of the lyric timing (`POST .../lyric-timing/resync`, spec §5.9). With lines set by hand,
 * `request` opens the confirm ("Re-sync drops your N changes"); with none, it starts at once.
 *
 * The answer (status `syncing`) goes into the cache at once, so the page shows the sync bar. The
 * server aligns again (it does not take the stored alignment); `catalog.song.updated` reads the
 * end. Then `note` says "Synced again" (or that no time changed). A refusal shows its sentence next
 * to the button (`note.kind === 'error'`): 429 names the limit of `LYRIC_RESYNC_PER_HOUR` an hour.
 */
export function useLyricResync({ accountId, itemUuid, timing, editedCount, discardPending }: UseLyricResyncProps) {
  const intl = useIntl();
  const dispatch = useDispatch<StoreType['dispatch']>();
  const [resyncTiming, { isLoading }] = useResyncLyricTimingMutation();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [note, setNote] = useState<LyricResyncNote | null>(null);
  // The line starts before the click, while a re-sync of this page runs; null otherwise.
  const startsBeforeRef = useRef<Array<number | null> | null>(null);
  // True once the page showed the `syncing` timing of this re-sync.
  const sawSyncingRef = useRef(false);
  const timingRef = useRef(timing);
  timingRef.current = timing;

  useEffect(() => {
    const before = startsBeforeRef.current;
    if (before === null || timing === undefined) {
      return;
    }
    if (timing?.status === 'syncing') {
      sawSyncingRef.current = true;
      return;
    }
    if (!sawSyncingRef.current) {
      return;
    }
    startsBeforeRef.current = null;
    sawSyncingRef.current = false;
    setNote(resyncDoneNote(before, timing));
  }, [timing]);

  useEffect(() => {
    if (note?.kind !== 'done') {
      return undefined;
    }
    const timer = setTimeout(() => setNote(null), RESYNC_DONE_NOTE_MS);
    return () => clearTimeout(timer);
  }, [note]);

  const start = useCallback(async (): Promise<void> => {
    discardPending();
    setNote(null);
    const startsBefore = lineStartsOf(timingRef.current);
    try {
      const answer = await resyncTiming({ accountId, itemUuid }).unwrap();
      startsBeforeRef.current = startsBefore;
      sawSyncingRef.current = false;
      dispatch(
        fymAPI.util.updateQueryData('getLyricTiming', { accountId, itemUuid }, (draft) => {
          draft.timing = answer.timing;
        })
      );
    } catch (error) {
      startsBeforeRef.current = null;
      const message = resyncRefusalMessage(lyricTimingRefusal(error));
      setNote({ kind: 'error', message: intl.formatMessage(message, { count: LYRIC_RESYNC_PER_HOUR }) });
    } finally {
      setIsConfirmOpen(false);
    }
  }, [accountId, discardPending, dispatch, intl, itemUuid, resyncTiming]);

  const request = useCallback(() => {
    setNote(null);
    if (editedCount > 0) {
      setIsConfirmOpen(true);
      return;
    }
    start().catch(console.error);
  }, [editedCount, start]);

  const confirm = useCallback(() => {
    start().catch(console.error);
  }, [start]);

  const cancel = useCallback(() => setIsConfirmOpen(false), []);

  return { request, confirm, cancel, isConfirmOpen, isBusy: isLoading, note };
}
